import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner } from "@phosphor-icons/react";

const APPEARANCE = {
  theme: "stripe", labels: "auto", inputs: "spaced",
  variables: { borderRadius: "4px", colorBackground: "#ffffff", colorDanger: "#df1b41", colorPrimary: "#0570de", colorSuccess: "#00c853", colorText: "#30313d", fontFamily: "default", fontSizeBase: "16px", spacingUnit: "4px" },
};

export function StripeCheckoutForm({ open, onClose, publishableKey, clientSecret, planName }) {
  const mountRef = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || !clientSecret || !publishableKey || !window.Stripe) return;
    let form;
    setReady(false); setError("");
    (async () => {
      try {
        const stripe = window.Stripe(publishableKey);
        const checkout = stripe.initCheckoutFormSdk({ clientSecret, appearance: APPEARANCE });
        form = checkout.createForm({ layout: "expanded" });
        for (let i = 0; i < 20 && !mountRef.current; i++) await new Promise((r) => setTimeout(r, 50));
        if (!mountRef.current) throw new Error("Checkout container not ready");
        form.mount(mountRef.current);
        setReady(true);
        const loaded = await checkout.loadActions();
        if (loaded.type === "success") {
          form.on("confirm", async (event) => {
            try { await loaded.actions.confirm({ formConfirmEvent: event }); }
            catch (e) { setError(e?.message || "Payment could not be confirmed. Please try again."); }
          });
        } else {
          setError(loaded.error?.message || "Unable to load checkout.");
        }
      } catch (e) { setError(e?.message || "Unable to load checkout."); }
    })();
    return () => { try { form?.unmount?.(); } catch (_) {} };
  }, [open, clientSecret, publishableKey]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="stripe-checkout-dialog">
        <DialogHeader>
          <DialogTitle>Subscribe to {planName}</DialogTitle>
          <DialogDescription>Secure card payment powered by Stripe.</DialogDescription>
        </DialogHeader>
        {!ready && !error && <div className="flex justify-center py-10"><Spinner size={28} className="animate-spin text-emerald-600" /></div>}
        {error && <p className="text-sm text-red-600" data-testid="stripe-checkout-error">{error}</p>}
        <div ref={mountRef} id="checkout-form" data-testid="stripe-checkout-form" />
      </DialogContent>
    </Dialog>
  );
}
